import {
	createFileRoute,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Suspense } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { z } from "zod";
import { Heading } from "@/components/heading";
import { Spinner } from "@/components/ui/spinner";
import {
	LessonContentForm,
	type LessonContentFormSchemaValues,
} from "@/forms/lesson-content-form";
import type { cursos_slugAulasNovaCreateClassMutation } from "./__generated__/cursos_slugAulasNovaCreateClassMutation.graphql";
import type { cursos_slugAulasNovaQuery } from "./__generated__/cursos_slugAulasNovaQuery.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/area-de-membros/cursos/$slug/aulas/nova",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			sectionId: z.string(),
		}),
	),
});

function RouteComponent() {
	return (
		<Suspense
			fallback={
				<div className="size-full flex items-center justify-center py-4 md:py-8">
					<Spinner className="size-8" />
				</div>
			}
		>
			<CreateLessonPage />
		</Suspense>
	);
}

function CreateLessonPage() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { slug } = Route.useParams();
	const { sectionId } = useSearch({ from: Route.id });
	const navigate = useNavigate();

	const data = useLazyLoadQuery<cursos_slugAulasNovaQuery>(
		graphql`
			query cursos_slugAulasNovaQuery($clubSlug: String!, $courseSlug: String!) {
				club(slug: $clubSlug) {
					course(slug: $courseSlug) {
						id
						name
						slug
						sections(status: ALL) {
							id
							name
						}
					}
				}
			}
		`,
		{
			clubSlug: club.slug,
			courseSlug: slug,
		},
	);

	const course = data.club.course;

	const sections = course.sections.map((s) => ({
		id: s.id,
		name: s.name,
	}));

	const [createClass, _isCreatingClass] =
		useMutation<cursos_slugAulasNovaCreateClassMutation>(graphql`
			mutation cursos_slugAulasNovaCreateClassMutation(
				$payload: CreateCourseClassInput!
			) {
				createCourseClass(payload: $payload) {
					id
					sections(status: ALL) {
						id
						classes(status: ALL) {
							__typename
							id
							name
							slug
							active
							position
						}
					}
				}
			}
		`);

	const handleSubmit = (values: LessonContentFormSchemaValues) => {
		createClass({
			variables: {
				payload: {
					course: course.id,
					section: values.sectionId,
					name: values.name,
					active: true,
					notifyMembers: values.notifyStudents,
					description: values.description
						? JSON.stringify(values.description)
						: null,
					video: values.videoFile?.id ?? null,
					externalVideoUrl: values.externalVideoUrl || null,
					attachments: values.attachments.map((a) => ({ file: a.id })),
				},
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Aula criada com sucesso!");
				navigate({
					to: "/c/$club/area-de-membros/cursos/$slug",
					params: { club: club.slug, slug: course.slug },
				});
			},
			onError: () => {
				toast.error("Erro ao criar aula. Tente novamente.");
			},
		});
	};

	return (
		<div className="size-full flex items-center justify-center py-4 md:py-8">
			<div className="p-6 w-full max-md:px-4 max-w-[700px] flex flex-col gap-6 border rounded-md">
				<Heading>Criar Aula de Conteúdo</Heading>

				<LessonContentForm
					onSubmit={handleSubmit}
					sections={sections}
					initialSectionId={sectionId}
				/>
			</div>
		</div>
	);
}
